/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Trash2, AlertTriangle } from 'lucide-react';
import DrugAutocomplete from '@/components/fse/DrugAutocomplete';

/**
 * PrescriptionItemRow — single drug row of the prescription form.
 * Extracted from PrescriptionCreator.jsx (Code Quality refactor).
 */
export const PrescriptionItemRow = ({ item, index, canRemove, duplicateActive, onChange, onRemove }) => (
  <div className="rounded-lg border border-border p-3 space-y-2" data-testid={`rx-item-${index}`}>
    <div className="flex items-center justify-between gap-2">
      <Badge variant="secondary" className="text-[10px]">Farmaco #{index + 1}</Badge>
      {canRemove && (
        <Button size="icon" variant="ghost" onClick={onRemove} data-testid={`rx-remove-${index}`} aria-label="Rimuovi">
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      )}
    </div>
    {duplicateActive && (
      <div className="flex items-start gap-1.5 rounded-md border border-amber-500/50 bg-amber-500/10 px-2 py-1.5 text-[11px] text-amber-800 dark:text-amber-200" data-testid={`rx-duplicate-${index}`}>
        <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
        <span>
          <b>Doppione rilevato</b>: il principio attivo <span className="font-mono">{duplicateActive}</span> risulta già nella terapia in corso del paziente. Verifica prima di prescriverlo nuovamente.
        </span>
      </div>
    )}
    <div className="grid gap-2 sm:grid-cols-[1.4fr_140px_140px]">
      <div>
        <Label className="text-[10px]">Nome commerciale / principio attivo *</Label>
        <DrugAutocomplete
          value={item.name}
          onChange={(v) => onChange({ name: v, active: v === item.name ? item.active : '' })}
          onSelect={(drug) => onChange({
            name: drug.name,
            aic: drug.aic || item.aic,
            active: (drug.active || '').toLowerCase(),
          })}
          placeholder="es. Ramipril 5 mg"
          testId={`rx-name-${index}`}
        />
      </div>
      <div>
        <Label className="text-[10px]">AIC (opzionale)</Label>
        <Input value={item.aic} onChange={(e) => onChange({ aic: e.target.value })}
               placeholder="es. 027935016" maxLength={20} data-testid={`rx-aic-${index}`} />
      </div>
      <div>
        <Label className="text-[10px]">Quantità *</Label>
        <Input value={item.quantity} onChange={(e) => onChange({ quantity: e.target.value })}
               placeholder="es. 1 scatola" maxLength={10} data-testid={`rx-qty-${index}`} />
      </div>
    </div>
    <div>
      <Label className="text-[10px]">Posologia *</Label>
      <Input value={item.posology} onChange={(e) => onChange({ posology: e.target.value })}
             placeholder="es. 1 compressa al mattino per 30 giorni" maxLength={400} data-testid={`rx-posology-${index}`} />
    </div>
    <div>
      <Label className="text-[10px]">Note</Label>
      <Input value={item.notes} onChange={(e) => onChange({ notes: e.target.value })}
             placeholder="es. Assumere a stomaco pieno" maxLength={200} data-testid={`rx-notes-${index}`} />
    </div>
  </div>
);
